/**
 * Where the legacy pages of the archive lead once the 3.0.0 archive is live (FE-A1).
 *
 * At the switch every legacy page of the archive that has a 3.0.0 successor opens the successor
 * instead, whatever leads there: the buttons of the property home, the side menu, the cards and the
 * notice of the Home, the bar of the mapping page, the bars of the legacy pages themselves, a bookmark.
 * The legacy route keeps its place, its component and its guards: `ArchiveV3RedirectGuard`, first in
 * its `canActivate`, sends the navigation on to the new page while the switch is on, and lets it
 * through unchanged while it is off (@see isArchiveV3Live). The new pages keep their own guards.
 *
 * A legacy route names its new page under `data.archiveV3Redirect` (@see ARCHIVE_V3_REDIRECT_DATA_KEY)
 * with one of the addresses below: the path under `/:lang/app`, one segment per entry, where a segment
 * starting with ":" is a parameter of the legacy route, carried over as it is. The query parameters
 * travel too; anything else of the legacy address (the section of `documents/:activeSection`) is left
 * behind, the new pages having no such parameter.
 *
 * @since 3.0.0
 */
export const ARCHIVE_V3_REDIRECT_DATA_KEY = "archiveV3Redirect";

/**
 * «Archivio fascicolo documentale»: `properties/:propertyId/documents`, `documents/:activeSection`, and
 * the guests' page `properties/my-property/:propertyId`, which the switch retires as well (FEX-29).
 *
 * @since 3.0.0
 */
export const ARCHIVE_V3_PROPERTY_DOCUMENTS: ReadonlyArray<string> = ["properties", ":propertyId", "documentsV3"];

/**
 * «Ricerca documenti» of a property: `properties/:propertyId/search`.
 *
 * @since 3.0.0
 */
export const ARCHIVE_V3_PROPERTY_SEARCH: ReadonlyArray<string> = ["properties", ":propertyId", "searchV3"];

/**
 * «Inserisci nuovo documento»: `properties/:propertyId/addDocuments` (master and collaborator) and
 * `addNewDocuments` (admin and operator), one page on the new archive.
 *
 * @since 3.0.0
 */
export const ARCHIVE_V3_PROPERTY_INSERT: ReadonlyArray<string> = ["properties", ":propertyId", "addNewDocumentsV3"];

/**
 * «Elenco documenti con scadenza»: `properties/:propertyId/documentsWithExpiration`.
 *
 * @since 3.0.0
 */
export const ARCHIVE_V3_PROPERTY_DEADLINES: ReadonlyArray<string> = [
  "properties",
  ":propertyId",
  "documentsWithExpirationV3",
];

/**
 * «Ultimi documenti inseriti» of a property: `properties/:propertyId/latestDocumentsInserted`.
 *
 * @since 3.0.0
 */
export const ARCHIVE_V3_PROPERTY_LATEST_DOCUMENTS: ReadonlyArray<string> = [
  "properties",
  ":propertyId",
  "latestDocumentsInsertedV3",
];

/**
 * «Inserisci documenti» of the side menu: `add-documents`.
 *
 * @since 3.0.0
 */
export const ARCHIVE_V3_GENERAL_INSERT: ReadonlyArray<string> = ["add-documentsV3"];

/**
 * «Ricerca» of the side menu and of the Home: `search-docs/listv2`.
 *
 * @since 3.0.0
 */
export const ARCHIVE_V3_GENERAL_SEARCH: ReadonlyArray<string> = ["search-docs", "listv3"];

/**
 * «Riepilogo documenti»: `summary-docs/list`.
 *
 * @since 3.0.0
 */
export const ARCHIVE_V3_SUMMARY: ReadonlyArray<string> = ["summary-docs-v3", "list"];

/**
 * «Inserimenti successivi»: `summary-docs/insertions-list`.
 *
 * @since 3.0.0
 */
export const ARCHIVE_V3_NEXT_INSERTIONS: ReadonlyArray<string> = ["summary-docs-v3", "insertions-list"];

/**
 * «Impostazioni» → «Documenti», the documents bin: `deleted-docs/list`.
 *
 * @since 3.0.0
 */
export const ARCHIVE_V3_TRASH: ReadonlyArray<string> = ["deleted-docs-v3", "list"];

/**
 * «Scadenze» → «Documenti» of the side menu and the «Scadenze» card of the Home:
 * `deadlines/list/documents`. The other deadline pages of that module stay where they are.
 *
 * @since 3.0.0
 */
export const ARCHIVE_V3_DEADLINES: ReadonlyArray<string> = ["deadlines-v3", "list"];

/**
 * «Storico documenti» of the side menu and the link of the yellow notice of the Home:
 * `historical-docs/list`.
 *
 * @since 3.0.0
 */
export const ARCHIVE_V3_LATEST_DOCUMENTS: ReadonlyArray<string> = ["historical-docs-v3", "list"];

/**
 * «Impostazioni» → «Documenti»: `settings/document-detail`, the editor of the old taxonomy, whose
 * saves the back-end refuses (410) once the old archive is switched off. Its successor is the editor
 * of the new taxonomy, «Document-template» (FEX-27).
 *
 * @since 3.0.0
 */
export const ARCHIVE_V3_SETTINGS_DOCUMENTS: ReadonlyArray<string> = ["settings", "document-template"];

/**
 * The commands that open the new page of a legacy route, absolute (`/`, the language, `app`, then the
 * address of the new page), or null to stay on the legacy page.
 *
 * Null while the archive is not live, for a route that names no new page, and for a name that cannot
 * be completed (not a list of segments, an empty segment, a parameter the address does not carry, no
 * language): the legacy page then opens as before, rather than an address that does not exist.
 *
 * @since 3.0.0
 *
 * @param live   Whether the 3.0.0 archive is live, @see isArchiveV3Live
 * @param target What the legacy route holds under `data.archiveV3Redirect`
 * @param params The parameters of the legacy route
 * @param lang   The language of the address
 */
export function archiveV3RedirectCommands(
  live: boolean,
  target: unknown,
  params: { [name: string]: unknown } | null | undefined,
  lang: string | null | undefined
): string[] | null {
  if (!live || !Array.isArray(target) || !target.length || typeof lang !== "string" || !lang) {
    return null;
  }

  const commands: string[] = ["/", lang, "app"];
  for (const segment of target) {
    if (typeof segment !== "string" || !segment) {
      return null;
    }
    if (segment.charAt(0) !== ":") {
      commands.push(segment);
      continue;
    }
    const value = params ? params[segment.slice(1)] : undefined;
    if (typeof value !== "string" || !value) {
      return null;
    }
    commands.push(value);
  }
  return commands;
}

/**
 * The route of a side-menu entry that opens a legacy page of the archive, in the form the menu writes
 * it (`/app/...`: the menu puts the language in front): the legacy route while the 3.0.0 archive is not
 * live, the address of the new page once it is.
 *
 * The legacy route alone would still reach the new page (@see ArchiveV3RedirectGuard), but the menu
 * marks the open entry by comparing its route with the address on screen: an entry left on its legacy
 * route would not be marked on the new page, or only by a chance prefix (`add-documents` of
 * `add-documentsV3`). Only an address with no parameter can be an entry of the menu: for any other
 * name (not a list of segments, an empty segment, a ":parameter") the legacy route stays.
 *
 * @since 3.0.0
 *
 * @param live        Whether the 3.0.0 archive is live, @see isArchiveV3Live
 * @param legacyRoute The route of the entry until the switch
 * @param target      The address of the new page, one of the ARCHIVE_V3_* above
 */
export function archiveV3MenuRoute(live: boolean, legacyRoute: string, target: unknown): string {
  if (!live || !Array.isArray(target) || !target.length) {
    return legacyRoute;
  }

  const segments: string[] = [];
  for (const segment of target) {
    if (typeof segment !== "string" || !segment || segment.charAt(0) === ":") {
      return legacyRoute;
    }
    segments.push(segment);
  }
  return "/app/" + segments.join("/");
}
